import * as React from 'react';
import Documents from '../homepage/Documents';
import moment from 'moment';
import InfoMessages from '../homepage/InfoMessages';
import i18n from '../../language/i18n';
import * as i18next from 'i18next';
import Spinner from '../Controls/Spinner';
import { useDocuments, useSetDocumentSortOrder, Document } from '../../hooks/useDocuments';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
    DataGridPremium,
    GridColDef,
    GridRenderCellParams,
    GridListViewColDef,
    GridRowOrderChangeParams,
} from '@mui/x-data-grid-premium';

function openDocument(url: string) {
    window.open(url, '_blank');
}

const RecentBadge = () => (
    <Chip
        size="small"
        label={i18next.t('site.recentlyUpdated')}
        data-testid="recently-updated"
        color="success"
        sx={{ height: 18, ml: 0.5, '& .MuiChip-label': { px: 1.0, fontSize: '0.65rem' } }}
    />
);

/** Returns true when the filename extension can be rendered inline in a browser. */
function isViewableInBrowser(filename: string): boolean {
    const ext = (filename || '').split('.').pop()?.toLowerCase() ?? '';
    return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
}

const ListViewCell = (params: GridRenderCellParams) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.75, width: '100%', gap: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {isViewableInBrowser(params.row.filename) ? (
                    <Typography
                        component={Link}
                        to={`/documents/${params.row.documentId}`}
                        sx={{ color: 'secondary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        {params.row.title}
                    </Typography>
                ) : (
                    <Typography
                        component="a"
                        sx={{ color: 'secondary.main', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); openDocument(params.row.documentUrl); }}
                    >
                        {params.row.title}
                    </Typography>
                )}
                {isRecentlyUpdated(params.row.updateDateUTC) && <RecentBadge />}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {moment(params.row.createdDate).format('L')}
            </Typography>
        </Box>
        {params.row.description && (
            <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {params.row.description}
            </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: 'min(40%, 20ch)', flexShrink: 0 }}>
                <Typography variant="caption" color="text.disabled">{i18n.t('document.carMake')}:</Typography>
                <Typography variant="caption" fontWeight={500} color={params.row.carMake ? 'text.secondary' : 'text.disabled'}>
                    {params.row.carMake || '·'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" color="text.disabled">{i18n.t('document.documentType')}:</Typography>
                <Typography variant="caption" fontWeight={500} color={params.row.documentType ? 'text.secondary' : 'text.disabled'}>
                    {params.row.documentType || '·'}
                </Typography>
            </Box>
        </Box>
    </Box>
);

const TitleCell = (params: GridRenderCellParams) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isViewableInBrowser(params.row.filename) ? (
            <Typography
                component={Link}
                to={`/documents/${params.row.documentId}`}
                sx={{ color: 'secondary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                {params.value}
            </Typography>
        ) : (
            <Typography
                component="a"
                sx={{ color: 'secondary.main', cursor: 'pointer' }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); openDocument(params.row.documentUrl); }}
            >
                {params.value}
            </Typography>
        )}
        {isRecentlyUpdated(params.row.updateDateUTC) && <RecentBadge />}
    </Box>
);

interface IProps {
    isAdminPage?: boolean;
    goToDocumentView?: (documentId?: number) => void;
}

function DocumentPage({ isAdminPage, goToDocumentView }: IProps) {
    const [documents, setDocuments] = React.useState<Document[]>([]);
    const [searchQuery, setSearchQuery] = React.useState('');

    const [filterDocTypeAnchorEl, setFilterDocTypeAnchorEl] = React.useState<HTMLElement | null>(null);
    const [filterCarMakeAnchorEl, setFilterCarMakeAnchorEl] = React.useState<HTMLElement | null>(null);
    const [selectedDocTypes, setSelectedDocTypes] = React.useState<string[]>([]);
    const [selectedCarMakes, setSelectedCarMakes] = React.useState<string[]>([]);
    const [driverSortField, setDriverSortField] = React.useState<'createdDate' | 'title' | 'carMake' | 'documentType' | null>(null);
    const [driverSortDir, setDriverSortDir] = React.useState<'asc' | 'desc'>('asc');
    const [adminSortField, setAdminSortField] = React.useState<'createdDate' | 'title' | 'carMake' | 'documentType' | null>('createdDate');
    const [adminSortDir, setAdminSortDir] = React.useState<'asc' | 'desc'>('desc');

    function toggleDriverSort(field: 'createdDate' | 'title' | 'carMake' | 'documentType') {
        if (driverSortField === field) {
            setDriverSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setDriverSortField(field);
            setDriverSortDir('asc');
        }
    }

    function toggleAdminSort(field: 'createdDate' | 'title' | 'carMake' | 'documentType') {
        if (adminSortField === field) {
            setAdminSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setAdminSortField(field);
            setAdminSortDir('asc');
        }
    }

    const { data: fetchedDocuments = [], isLoading } = useDocuments();
    const setSortOrderMutation = useSetDocumentSortOrder();

    React.useEffect(() => {
        if (fetchedDocuments.length > 0) setDocuments(fetchedDocuments);
    }, [fetchedDocuments]);

    const uniqueDocTypes = React.useMemo(
        () => [...new Set(fetchedDocuments.map(d => d.documentType).filter((t): t is string => !!t))],
        [fetchedDocuments],
    );

    const uniqueCarMakes = React.useMemo(
        () => [...new Set(fetchedDocuments.map(d => d.carMake).filter((m): m is string => !!m))],
        [fetchedDocuments],
    );

    const filteredDocuments = React.useMemo(() => {
        let docs = fetchedDocuments;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            docs = docs.filter(
                (doc) =>
                    doc.title?.toLowerCase().includes(q) ||
                    doc.description?.toLowerCase().includes(q) ||
                    (doc.carMake ?? '').toLowerCase().includes(q),
            );
        }
        if (selectedDocTypes.length > 0) {
            docs = docs.filter(d => selectedDocTypes.includes(d.documentType ?? ''));
        }
        if (selectedCarMakes.length > 0) {
            docs = docs.filter(d => selectedCarMakes.includes(d.carMake ?? ''));
        }
        if (driverSortField) {
            docs = [...docs].sort((a, b) => {
                const dir = driverSortDir === 'asc' ? 1 : -1;
                if (driverSortField === 'createdDate') {
                    return dir * (new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
                }
                const aVal = (a[driverSortField] ?? '') as string;
                const bVal = (b[driverSortField] ?? '') as string;
                return dir * aVal.localeCompare(bVal);
            });
        }
        return docs;
    }, [fetchedDocuments, searchQuery, selectedDocTypes, selectedCarMakes, driverSortField, driverSortDir]);

    const adminGridRows = React.useMemo(() => {
        let rows = documents;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            rows = rows.filter(
                (doc) =>
                    doc.title?.toLowerCase().includes(q) ||
                    doc.description?.toLowerCase().includes(q) ||
                    (doc.carMake ?? '').toLowerCase().includes(q),
            );
        }
        if (selectedDocTypes.length > 0) {
            rows = rows.filter(d => selectedDocTypes.includes(d.documentType ?? ''));
        }
        if (selectedCarMakes.length > 0) {
            rows = rows.filter(d => selectedCarMakes.includes(d.carMake ?? ''));
        }
        if (adminSortField) {
            rows = [...rows].sort((a, b) => {
                const dir = adminSortDir === 'asc' ? 1 : -1;
                if (adminSortField === 'createdDate') {
                    return dir * (new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
                }
                const aVal = (a[adminSortField] ?? '') as string;
                const bVal = (b[adminSortField] ?? '') as string;
                return dir * aVal.localeCompare(bVal);
            });
        }
        return rows;
    }, [documents, searchQuery, selectedDocTypes, selectedCarMakes, adminSortField, adminSortDir]);

    function setNewSortOrders(docs: Document[]): Document[] {
        return docs.map((doc, index) => ({ ...doc, sortOrder: index + 1 }));
    }

    function onRowOrderChange(params: GridRowOrderChangeParams) {
        const newDocuments = [...documents];
        const [removed] = newDocuments.splice(params.oldIndex, 1);
        newDocuments.splice(params.targetIndex, 0, removed);
        const withSortOrders = setNewSortOrders(newDocuments);
        setDocuments(withSortOrders);
        setSortOrderMutation.mutate(
            withSortOrders.map(d => ({ documentId: d.documentId, sortOrder: d.sortOrder } as Document))
        );
    }

    function toggleDocType(type: string) {
        setSelectedDocTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    }

    function toggleCarMake(make: string) {
        setSelectedCarMakes(prev =>
            prev.includes(make) ? prev.filter(m => m !== make) : [...prev, make]
        );
    }

    const adminColumns: GridColDef<Document>[] = [
        {
            field: 'createdDate',
            headerName: i18n.t('document.date'),
            flex: 1,
            valueFormatter: (value: Date) => moment(value).format('YYYY-MM-DD'),
        },
        {
            field: 'title',
            headerName: i18n.t('document.name'),
            flex: 3,
            renderCell: TitleCell,
        },
        {
            field: 'description',
            headerName: i18n.t('document.description'),
            flex: 3,
        },
        {
            field: 'carMake',
            headerName: i18n.t('document.carMake'),
            flex: 1,
            renderHeader: (params) => (
                <Box
                    onClick={() => toggleAdminSort('carMake')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {params.colDef.headerName}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="filter"
                        onClick={(e) => { e.stopPropagation(); setFilterCarMakeAnchorEl(e.currentTarget); }}
                        color={selectedCarMakes.length > 0 ? 'primary' : 'default'}
                    >
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            field: 'documentType',
            headerName: i18n.t('document.documentType'),
            flex: 1,
            renderHeader: (params) => (
                <Box
                    onClick={() => toggleAdminSort('documentType')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {params.colDef.headerName}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="filter"
                        onClick={(e) => { e.stopPropagation(); setFilterDocTypeAnchorEl(e.currentTarget); }}
                        color={selectedDocTypes.length > 0 ? 'primary' : 'default'}
                    >
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <IconButton size="small" color="primary" onClick={() => goToDocumentView?.(params.row.documentId)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const driverColumns: GridColDef<Document>[] = [
        {
            field: 'createdDate',
            headerName: i18n.t('document.date'),
            flex: 1,
            valueFormatter: (value: Date) => moment(value).format('L'),
        },
        {
            field: 'title',
            headerName: i18n.t('document.name'),
            flex: 3,
            renderCell: TitleCell,
        },
        {
            field: 'description',
            headerName: i18n.t('document.description'),
            flex: 2,
        },
        {
            field: 'carMake',
            headerName: i18n.t('document.carMake'),
            flex: 1,
            renderHeader: (params) => (
                <Box
                    onClick={() => toggleDriverSort('carMake')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {params.colDef.headerName}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="filter"
                        onClick={(e) => { e.stopPropagation(); setFilterCarMakeAnchorEl(e.currentTarget); }}
                        color={selectedCarMakes.length > 0 ? 'primary' : 'default'}
                    >
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            field: 'documentType',
            headerName: i18n.t('document.documentType'),
            flex: 1,
            renderHeader: (params) => (
                <Box
                    onClick={() => toggleDriverSort('documentType')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {params.colDef.headerName}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="filter"
                        onClick={(e) => { e.stopPropagation(); setFilterDocTypeAnchorEl(e.currentTarget); }}
                        color={selectedDocTypes.length > 0 ? 'primary' : 'default'}
                    >
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const theme = useTheme();
    const isListView = useMediaQuery(theme.breakpoints.down('md'));
    const listViewColDef: GridListViewColDef = {
      field: 'listColumn',
      renderCell: ListViewCell,
    };

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: isAdminPage ? 12 : 8 }}>
                {isLoading ? <Spinner /> : (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h5" fontWeight={600}>
                                {i18n.t('document.header')}
                            </Typography>
                            {isAdminPage && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={() => goToDocumentView?.()}
                                >
                                    {i18n.t('documentedit.add')}
                                </Button>
                            )}
                        </Box>

                        <TextField
                            size="small"
                            fullWidth
                            placeholder={i18n.t('document.search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            slotProps={{
                                htmlInput: { role: 'searchbox' },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ mb: 2 }}
                        />

                        {(selectedDocTypes.length > 0 || selectedCarMakes.length > 0) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {selectedCarMakes.map(make => (
                                    <Chip
                                        key={make}
                                        label={make}
                                        size="small"
                                        onDelete={() => toggleCarMake(make)}
                                    />
                                ))}
                                {selectedDocTypes.map(type => (
                                    <Chip
                                        key={type}
                                        label={type}
                                        size="small"
                                        onDelete={() => toggleDocType(type)}
                                    />
                                ))}
                                {(selectedDocTypes.length + selectedCarMakes.length) > 1 && (
                                    <Chip
                                        label={i18n.t('document.clearFilters')}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => { setSelectedDocTypes([]); setSelectedCarMakes([]); }}
                                    />
                                )}
                            </Box>
                        )}

                        {isAdminPage ? (
                            <DataGridPremium
                                rows={adminGridRows}
                                columns={adminColumns}
                                getRowId={(row) => row.documentId}
                                rowReordering
                                onRowOrderChange={onRowOrderChange}
                                disableColumnMenu
                                disableColumnResize
                                hideFooter
                                density="compact"
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                            />
                        ) : (
                            <>
                                <Box sx={{ display: { xs: 'flex', md: 'none' }, border: '1px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.paper' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1.0, width: '100%', gap: 4.0, overflow: 'auto' }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            onClick={() => toggleDriverSort('createdDate')}
                                            sx={{ color: driverSortField === 'createdDate' ? 'primary.main' : 'text.secondary', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                        >
                                            {i18n.t('document.date')}{driverSortField === 'createdDate' ? (driverSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            onClick={() => toggleDriverSort('title')}
                                            sx={{ color: driverSortField === 'title' ? 'primary.main' : 'text.secondary', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', flexGrow: 1 }}
                                        >
                                            {i18n.t('document.name')}{driverSortField === 'title' ? (driverSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="caption"
                                                fontWeight={500}
                                                onClick={() => toggleDriverSort('carMake')}
                                                sx={{ color: driverSortField === 'carMake' ? 'primary.main' : 'text.secondary', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {i18n.t('document.carMake')}{driverSortField === 'carMake' ? (driverSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => setFilterCarMakeAnchorEl(e.currentTarget)}
                                                color={selectedCarMakes.length > 0 ? 'primary' : 'default'}
                                                sx={{ p: 0.25 }}
                                            >
                                                <FilterListIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="caption"
                                                fontWeight={500}
                                                onClick={() => toggleDriverSort('documentType')}
                                                sx={{ color: driverSortField === 'documentType' ? 'primary.main' : 'text.secondary', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {i18n.t('document.documentType')}{driverSortField === 'documentType' ? (driverSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => setFilterDocTypeAnchorEl(e.currentTarget)}
                                                color={selectedDocTypes.length > 0 ? 'primary' : 'default'}
                                                sx={{ p: 0.25 }}
                                            >
                                                <FilterListIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                                <DataGridPremium
                                    rows={filteredDocuments}
                                    columns={driverColumns}
                                    getRowId={(row) => row.documentId}
                                    disableColumnMenu
                                    disableColumnResize
                                    hideFooter
                                    density="compact"
                                    listView={isListView}
                                    listViewColumn={listViewColDef}
                                    getRowHeight={() => isListView ? 'auto' : undefined}
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                />
                            </>
                        )}

                        <Popover
                            open={Boolean(filterDocTypeAnchorEl)}
                            anchorEl={filterDocTypeAnchorEl}
                            onClose={() => setFilterDocTypeAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <Box sx={{ minWidth: 180, maxHeight: 300, overflow: 'auto', py: 0.5 }}>
                                <ul role="listbox" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                    {uniqueDocTypes.length === 0 ? (
                                        <li style={{ padding: '8px 16px', color: 'rgba(0,0,0,0.6)', fontSize: '0.875rem' }}>
                                            Inga typer
                                        </li>
                                    ) : (
                                        uniqueDocTypes.map(type => (
                                            <li
                                                key={type}
                                                role="option"
                                                aria-selected={selectedDocTypes.includes(type)}
                                                onClick={() => toggleDocType(type)}
                                                style={{ padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedDocTypes.includes(type)}
                                                    onChange={() => {}}
                                                    sx={{ pointerEvents: 'none' }}
                                                />
                                                <Typography variant="body2">{type}</Typography>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </Box>
                        </Popover>

                        <Popover
                            open={Boolean(filterCarMakeAnchorEl)}
                            anchorEl={filterCarMakeAnchorEl}
                            onClose={() => setFilterCarMakeAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <Box sx={{ minWidth: 180, maxHeight: 300, overflow: 'auto', py: 0.5 }}>
                                <ul role="listbox" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                    {uniqueCarMakes.length === 0 ? (
                                        <li style={{ padding: '8px 16px', color: 'rgba(0,0,0,0.6)', fontSize: '0.875rem' }}>
                                            Inga märken
                                        </li>
                                    ) : (
                                        uniqueCarMakes.map(make => (
                                            <li
                                                key={make}
                                                role="option"
                                                aria-selected={selectedCarMakes.includes(make)}
                                                onClick={() => toggleCarMake(make)}
                                                style={{ padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedCarMakes.includes(make)}
                                                    onChange={() => {}}
                                                    sx={{ pointerEvents: 'none' }}
                                                />
                                                <Typography variant="body2">{make}</Typography>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </Box>
                        </Popover>
                    </Box>
                )}
            </Grid>

            {!isAdminPage && (
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <InfoMessages />
                    <Box sx={{ mt: 9 }}>
                        <Documents />
                    </Box>
                </Grid>
            )}
        </Grid>
    );
}

export default DocumentPage;
